import { Link } from 'react-router-dom'
import { InputHTMLAttributes, useEffect, useRef, useState, useCallback, FormEvent } from 'react'
import { IconBaseProps } from 'react-icons/lib'
import { FiAlertCircle } from 'react-icons/fi'
import { useField } from '@unform/core'

import { Container, Error, InformationTip, LabelInput } from './styles'
import Tooltip from '../Tooltip'

import { AiFillEyeInvisible, AiFillEye, AiOutlineQuestionCircle } from 'react-icons/ai'

import {
  cepMask,
  cpfMask,
  creditCard,
  creditCardDateMask,
  dateMask,
  telMask,
} from '../../utils/masks'
interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string
  icon?: React.ComponentType<IconBaseProps>
  isPass?: boolean
  type?: string
  color?: string
  link?: {
    label: string
    url: string
  }
  info?: string
  information?: string
  isCpf?: boolean
  isCep?: boolean
  isCreditCard?: boolean
  isCvv?: boolean
  isDateCreditCard?: boolean
  isPhone?: boolean
  isDate?: boolean
  label?: string
  className?: string
}

const Input: React.FC<InputProps> = ({
  icon: Icon,
  type,
  name,
  color,
  isPass,
  link,
  info,
  information,
  isCpf,
  isCep,
  isCreditCard,
  isDateCreditCard,
  isPhone,
  isDate,
  isCvv,
  label,
  className,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isFilled, setIsFilled] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const { fieldName, defaultValue, registerField, error } = useField(name)

  const handleInputBlur = useCallback(() => {
    setIsFocused(false)

    setIsFilled(!!inputRef.current?.value)
  }, [])

  const handleInputFocus = useCallback(() => {
    setIsFocused(true)
  }, [])

  const handleType = useCallback(() => {
    if (type === 'password' && !showPass) return 'password'
    if (type === 'password' && showPass) return 'text'

    return type
  }, [showPass, type])

  const handleKeyUp = useCallback(
    (e: FormEvent<HTMLInputElement>) => {
      if (isCpf) {
        e.currentTarget.maxLength = 14
        const value = cpfMask(e.currentTarget.value)
        e.currentTarget.value = value
      }

      if (isCep) {
        e.currentTarget.maxLength = 10
        const value = cepMask(e.currentTarget.value)
        e.currentTarget.value = value
      }

      if (isCreditCard) {
        e.currentTarget.maxLength = 19
        const value = creditCard(e.currentTarget.value)
        e.currentTarget.value = value
      }
      if (isDateCreditCard) {
        e.currentTarget.maxLength = 5
        const value = creditCardDateMask(e.currentTarget.value)
        e.currentTarget.value = value
      }

      if (isPhone) {
        e.currentTarget.maxLength = 15
        const value = telMask(e.currentTarget.value)
        e.currentTarget.value = value
      }

      if (isDate) {
        e.currentTarget.maxLength = 10
        const value = dateMask(e.currentTarget.value)
        e.currentTarget.value = value
      }

      if (isCvv) {
        e.currentTarget.maxLength = 4
        const value = creditCard(e.currentTarget.value)
        e.currentTarget.value = value
      }

      if (type === 'email') {
        const value = e.currentTarget.value.toLocaleLowerCase()
        e.currentTarget.value = value
      }
    },
    [isCpf, isCep, isCreditCard, isDateCreditCard, isPhone, isDate, isCvv, type]
  )

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    })
  }, [fieldName, registerField])
  return (
    <>
      {label ? <LabelInput>{label}</LabelInput> : ''}
      <Container
        isFilled={isFilled}
        isFocused={isFocused}
        isErrored={!!error}
        color={color}
        className={className}
      >
        {Icon && <Icon size={20} />}
        <input
          defaultValue={defaultValue}
          ref={inputRef}
          {...rest}
          type={handleType()}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyUp={handleKeyUp}
        />
        {error && (
          <Error title={error}>
            <FiAlertCircle color="#C53030" size={20} />
          </Error>
        )}
        {isPass && (
          <div onClick={() => setShowPass(!showPass)} id="userLogin">
            {showPass ? (
              <AiFillEyeInvisible size={20} id="userLogin" />
            ) : (
              <AiFillEye size={20} id="userLogin" />
            )}
          </div>
        )}

        {info && (
          <Tooltip title={info}>
            <AiOutlineQuestionCircle size={17} />
          </Tooltip>
        )}

        {information && (
          <InformationTip title={information}>
            <FiAlertCircle size={17} color="#c6c6c6" />
          </InformationTip>
        )}

        {link && (
          <Link to={link.url} target="_blank" rel="noreferrer">
            {link.label}
          </Link>
        )}
      </Container>
    </>
  )
}

export default Input
